import moment from "moment/moment";
import React from "react";
import { Link } from "react-router-dom";
import { FiActivity, FiDollarSign, FiDownload, FiMessageCircle, FiPercent, FiSend, FiTrendingUp } from "react-icons/fi";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Layout from "../../../components/layouts/Layout";
import {
  exportAutomationAnalyticsCsv,
  fetchConversionsMonthlyOverview,
} from "../AutomationTemplates/automationTemplateApi";
import SmsAccessGuard from "../Components/SmsAccessGuard";
import "../style/sms-billing.css";

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const formatNumber = (value) => {
  if (!hasValue(value) || Number.isNaN(Number(value))) return "—";

  return Number(value).toLocaleString();
};

const formatMoney = (value, currency) => {
  if (!hasValue(value) || Number.isNaN(Number(value))) return "—";

  const amount = Number(value);
  const currencyCode = currency || "AUD";

  try {
    if (typeof Intl !== "undefined" && Intl.NumberFormat) {
      return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: currencyCode,
      }).format(amount);
    }
  } catch (error) {
    // Fall back when an API currency code is unsupported by the browser.
  }

  return `${currencyCode} ${amount.toFixed(2)}`;
};

const getErrorMessage = (error) => {
  const responseMessage = error && error.response && error.response.data && error.response.data.message;
  const analyticsMessage = error && error.analyticsMessage;

  if (typeof responseMessage === "string" && responseMessage.trim()) return responseMessage;
  if (typeof analyticsMessage === "string" && analyticsMessage.trim()) return analyticsMessage;

  return "Automation analytics are unavailable right now.";
};

const invalidResponseError = (payload) => {
  const error = new Error("Invalid automation analytics response");

  error.analyticsMessage = payload && payload.message;

  return error;
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

class AutomationAnalytics extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedMonth: "",
      analytics: null,
      loadingAnalytics: false,
      exportingCsv: false,
      error: "",
    };

    this.isMountedFlag = false;
    this.requestId = 0;
  }

  componentDidMount() {
    this.isMountedFlag = true;
    this.loadAnalytics();
  }

  componentWillUnmount() {
    this.isMountedFlag = false;
  }

  loadAnalytics = async (month) => {
    const requestId = ++this.requestId;

    this.setState({ loadingAnalytics: true, error: "" });

    try {
      const response = await fetchConversionsMonthlyOverview(month ? { month } : undefined);
      const payload = response && response.data;
      const analytics = payload && payload.data;

      if (!analytics || !analytics.billing_month || payload.success === false) {
        throw invalidResponseError(payload);
      }

      if (this.isMountedFlag && requestId === this.requestId) {
        this.setState({
          selectedMonth: analytics.billing_month.value || month || "",
          analytics,
          loadingAnalytics: false,
          error: "",
        });
      }
    } catch (error) {
      if (this.isMountedFlag && requestId === this.requestId) {
        this.setState((state) => ({
          selectedMonth:
            state.analytics && state.analytics.billing_month
              ? state.analytics.billing_month.value
              : state.selectedMonth,
          loadingAnalytics: false,
          error: getErrorMessage(error),
        }));
      }
    }
  };

  handleMonthChange = (event) => {
    const selectedMonth = event.target.value;

    this.setState({ selectedMonth }, () => this.loadAnalytics(selectedMonth));
  };

  handleExportCsv = async () => {
    const { analytics, exportingCsv } = this.state;
    const month = analytics && analytics.billing_month && analytics.billing_month.value;
    const suppliedUrl = analytics && analytics.export && analytics.export.csv_url;
    const safeUrl = typeof suppliedUrl === "string" && suppliedUrl.indexOf("/api/v1/") === 0
      ? suppliedUrl.replace(/^\/api\/v1\//, "")
      : "";

    if (!month || exportingCsv) return;

    this.setState({ exportingCsv: true, error: "" });

    try {
      const response = await exportAutomationAnalyticsCsv(safeUrl, { month });

      downloadBlob(response.data, `automation-analytics-${month}.csv`);
    } catch (error) {
      if (this.isMountedFlag) {
        this.setState({ error: "The automation analytics CSV could not be downloaded. Please try again." });
      }
    } finally {
      if (this.isMountedFlag) this.setState({ exportingCsv: false });
    }
  };

  renderInitialState() {
    if (this.state.loadingAnalytics) {
      return (
        <div className="card radius-10">
          <div className="card-body py-5 text-center">
            <span className="spinner-border" role="status" aria-label="Loading automation analytics" />
          </div>
        </div>
      );
    }

    return (
      <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
        <span>{this.state.error || "Automation analytics are unavailable right now."}</span>
        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => this.loadAnalytics()}>
          Retry
        </button>
      </div>
    );
  }

  renderAnalytics() {
    const { analytics, selectedMonth, loadingAnalytics, exportingCsv, error } = this.state;
    const billingMonth = analytics.billing_month || {};
    const availableMonths = Array.isArray(analytics.available_months) ? analytics.available_months : [];
    const overview = analytics.overview || {};
    const usage = analytics.usage_breakdown || {};
    const billing = analytics.billing_status_breakdown || {};
    const currency = analytics.currency || "AUD";
    const dailyActivity = Array.isArray(analytics.daily_activity)
      ? analytics.daily_activity.map((day) => ({
          ...day,
          label: moment(day.date).isValid() ? moment(day.date).format("MMM D") : day.date,
          sms_sent: Number(day.sms_sent || 0),
          replies_received: Number(day.replies_received || 0),
          amount_charged: Number(day.amount_charged || 0),
        }))
      : [];
    const smsSent = Number(overview.sms_sent || 0);
    const repliesReceived = Number(overview.replies_received || 0);
    const replyRate = smsSent > 0 ? (repliesReceived / smsSent) * 100 : 0;
    const summaryCards = [
      { label: "SMS Sent", value: formatNumber(overview.sms_sent), icon: <FiSend />, tone: "gold" },
      { label: "Customer Replies", value: formatNumber(overview.replies_received), icon: <FiMessageCircle />, tone: "blue" },
      { label: "Reply Rate", value: `${replyRate.toFixed(1)}%`, icon: <FiPercent />, tone: "green" },
      { label: "Total Charged", value: formatMoney(overview.total_charged, currency), icon: <FiDollarSign />, tone: "violet" },
    ];
    const usageCards = [
      { label: "Automation SMS", value: Number(usage.automation_sms || 0) },
      { label: "Manual Outbound SMS", value: Number(usage.manual_outbound_sms || 0) },
      { label: "Scheduled SMS", value: Number(usage.scheduled_sms || 0) },
      { label: "Failed SMS", value: Number(usage.failed_sms || 0) },
    ];
    const billingCards = [
      { key: "unbilled", label: "Unbilled" },
      { key: "invoiced", label: "Invoiced" },
      { key: "paid", label: "Paid" },
      { key: "void", label: "Void" },
    ];
    const billingColours = ["#d3a13d", "#4f8cc0", "#57a36d", "#8a83b8"];
    const billingPieData = billingCards.map((card, index) => ({
      ...card,
      value: Number((billing[card.key] && billing[card.key].reply_count) || 0),
      amount: Number((billing[card.key] && billing[card.key].amount) || 0),
      colour: billingColours[index],
    }));
    const totalBillingReplies = billingPieData.reduce((total, item) => total + item.value, 0);
    const maxUsage = Math.max(...usageCards.map((card) => card.value), 1);
    const busiestDay = dailyActivity.reduce((highest, day) => (
      !highest || day.replies_received > highest.replies_received ? day : highest
    ), null);
    const automationShare = smsSent > 0 ? (Number(usage.automation_sms || 0) / smsSent) * 100 : 0;
    const insights = [
      { icon: <FiTrendingUp />, title: `Your reply rate is ${replyRate.toFixed(1)}%`, text: `${formatNumber(repliesReceived)} replies from ${formatNumber(smsSent)} messages.` },
      { icon: <FiActivity />, title: busiestDay ? `Most replies came on ${busiestDay.label}` : "No reply activity yet", text: busiestDay ? `${formatNumber(busiestDay.replies_received)} customer replies were received.` : "Activity will appear here when customers reply." },
      { icon: <FiSend />, title: "Automations are driving conversations", text: `${automationShare.toFixed(1)}% of sent messages came from automation flows.` },
      { icon: <FiDollarSign />, title: "Current outstanding balance", text: formatMoney(overview.current_outstanding_balance, currency) },
    ];
    const tabs = [
      ["Overview", "/conversions"],
      ["Conversations", "/conversions/conversations"],
      ["Notifications", "/conversions/notifications"],
      ["Analytics", "/conversions/analytics", true],
      ["My Automation Flows", "/conversions/automation-flows"],
      ["Browse Templates", "/conversions/sms-automation-templates"],
      ["Billing", "/conversions/billing"],
    ];

    return (
      <div className="automation-analytics-content analytics-dashboard-page">
        {error ? <div className="conversations-page-alert" role="alert">{error}</div> : null}
        <header className="analytics-dashboard-header">
          <div><h1>Analytics</h1><p>Understand how your automated conversations are performing.</p></div>
          <div className="analytics-dashboard-controls">
            <label>
              <select
                aria-label="Analytics month"
                value={selectedMonth}
                disabled={loadingAnalytics}
                onChange={this.handleMonthChange}
              >
                {availableMonths.map((month) => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </label>
              <button
                type="button"
                disabled={loadingAnalytics || exportingCsv}
                onClick={this.handleExportCsv}
              >
                <FiDownload /> {exportingCsv ? "Exporting..." : "Export CSV"}
              </button>
          </div>
        </header>

        <nav className="conversations-tabs analytics-dashboard-tabs" aria-label="Conversations sections">
          {tabs.map(([label, path, active]) => <Link key={path} to={path} className={active ? "active" : ""}>{label}</Link>)}
        </nav>

        <div className="automation-analytics-loading-wrap">
          {loadingAnalytics ? (
            <div className="conversion-loading-overlay" aria-live="polite">
              <span className="spinner-border" role="status" aria-label="Loading selected analytics month" />
            </div>
          ) : null}

          <section className="analytics-summary-grid">
            {summaryCards.map((card) => (
              <article className="analytics-summary-card" key={card.label}>
                <span className={`analytics-summary-icon ${card.tone}`}>{card.icon}</span>
                <div><span>{card.label}</span><strong>{card.value}</strong><small>{billingMonth.label || selectedMonth}</small></div>
              </article>
            ))}
          </section>

          <section className="analytics-dashboard-panel daily-activity-panel">
            <header><h2>Daily Activity</h2><select aria-label="Analytics interval" disabled><option>Daily</option></select></header>
            <div className="daily-activity-chart-wrap">
              {dailyActivity.length ? (
                <div className="analytics-daily-chart" role="img" aria-label={`Daily SMS activity for ${billingMonth.label || selectedMonth}`}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dailyActivity} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                      <CartesianGrid stroke="#eceeef" vertical={false} />
                      <XAxis dataKey="label" minTickGap={20} tick={{ fontSize: 11, fill: "#737b85" }} />
                      <YAxis yAxisId="counts" allowDecimals={false} tick={{ fontSize: 11, fill: "#737b85" }} />
                      <YAxis yAxisId="charge" orientation="right" tickFormatter={(value) => formatMoney(value, currency)} tick={{ fontSize: 10, fill: "#737b85" }} />
                      <Tooltip formatter={(value, name) => name === "Amount Charged" ? formatMoney(value, currency) : formatNumber(value)} />
                      <Legend />
                      <Line yAxisId="counts" type="monotone" dataKey="sms_sent" name="SMS Sent" stroke="#ce9629" strokeWidth={2} dot={{ r: 2 }} />
                      <Line yAxisId="counts" type="monotone" dataKey="replies_received" name="Customer Replies" stroke="#397eb8" strokeWidth={2} dot={{ r: 2 }} />
                      <Line yAxisId="charge" type="monotone" dataKey="amount_charged" name="Amount Charged" stroke="#398c59" strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="analytics-empty-state">No daily activity found for this month.</div>
              )}
            </div>
          </section>

          <div className="analytics-bottom-grid">
            <section className="analytics-dashboard-panel analytics-usage-panel">
              <header><h2>SMS Type Breakdown</h2></header>
              <div className="analytics-usage-bars">
                {usageCards.map((card) => <div className="analytics-usage-row" key={card.label}>
                  <span>{card.label}</span><div><i style={{ width: `${(card.value / maxUsage) * 100}%` }} /></div><strong>{formatNumber(card.value)}</strong>
                </div>)}
              </div>
              <Link to="/conversions/automation-flows" className="analytics-panel-link">View All Automation Flows →</Link>
            </section>

            <section className="analytics-dashboard-panel analytics-billing-panel">
              <header><h2>Billing Status Breakdown</h2></header>
              <div className="analytics-billing-content">
                <div className="analytics-donut-wrap">
                  {totalBillingReplies ? <ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={billingPieData} dataKey="value" innerRadius={48} outerRadius={72} paddingAngle={1}>{billingPieData.map((item) => <Cell key={item.key} fill={item.colour} />)}</Pie></PieChart></ResponsiveContainer> : <div className="analytics-empty-donut" />}
                  <div><strong>{formatNumber(totalBillingReplies)}</strong><span>Total Replies</span></div>
                </div>
                <ul>{billingPieData.map((item) => <li key={item.key}><i style={{ background: item.colour }} /><span>{item.label}</span><strong>{formatNumber(item.value)}</strong><small>{formatMoney(item.amount, currency)}</small></li>)}</ul>
              </div>
            </section>

            <section className="analytics-dashboard-panel analytics-insights-panel">
              <header><h2>Insights</h2></header>
              <div>{insights.map((insight) => <article key={insight.title}><span>{insight.icon}</span><div><strong>{insight.title}</strong><p>{insight.text}</p></div></article>)}</div>
            </section>
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <SmsAccessGuard>
        <Layout>
          <div className="page">
            {this.state.analytics ? this.renderAnalytics() : this.renderInitialState()}
          </div>
        </Layout>
      </SmsAccessGuard>
    );
  }
}

export default AutomationAnalytics;
